import type {
  BulkMessage,
  BulkMessageChannel,
  BulkMessageEvent,
  BulkMessageEventOutcome,
  BulkMessageMultimedia,
  BulkMessageQueueStatus,
  BulkMessageQueueStatusDto,
  BulkMessageStatus,
  CreateBulkMessagePayload,
  PageBulkMessageEvent,
} from '../types/bulk-message.types';

function parseNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return fallback;
}

function parseBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }

  return fallback;
}

function readString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
}

function parseBulkMessageChannel(value: unknown): BulkMessageChannel {
  const channel = readString(value);
  switch (channel) {
    case 'WHATSAPP_BUSINESS':
    case 'WHATSAPP':
    case 'WHATSAPP_CON_IMMAGINE':
    case 'EMAIL':
      return channel;
    default:
      return 'WHATSAPP';
  }
}

function parseBulkMessageStatus(value: unknown): BulkMessageStatus {
  const status = readString(value);
  switch (status) {
    case 'CREATA':
    case 'IN_CORSO':
    case 'TERMINATA':
    case 'IN_PAUSA':
      return status;
    default:
      return 'CREATA';
  }
}

function parseBulkMessageEventOutcome(value: unknown): BulkMessageEventOutcome {
  const outcome = readString(value);
  switch (outcome) {
    case 'NOT_REQUIRED':
    case 'PENDING':
    case 'INVIATO':
    case 'ERRORE_INVIO':
      return outcome;
    default:
      return 'PENDING';
  }
}

function parseBulkMessageQueueStatus(value: unknown): BulkMessageQueueStatus {
  const status = readString(value);
  switch (status) {
    case 'PENDING':
    case 'QUEUED':
    case 'PROCESSING':
    case 'PROCESSED':
    case 'TERMINATED_WITH_ERROR':
      return status;
    default:
      return 'PENDING';
  }
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => readString(item)).filter(Boolean);
}

function readNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => parseNumber(item, Number.NaN))
    .filter((item) => !Number.isNaN(item));
}

export function normalizeBulkMessageEvent(
  raw: unknown,
): BulkMessageEvent | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;

  return {
    id: parseNumber(item.id),
    bulkMessageCode: readString(item.bulkMessageCode),
    customerId: parseNumber(item.customerId),
    customerFirstName: readString(item.customerFirstName),
    customerLastName: readString(item.customerLastName),
    email: readString(item.email),
    prefix: readString(item.prefix),
    phone: readString(item.phone),
    title: readString(item.title),
    message: readString(item.message),
    outcomeSms: parseBulkMessageEventOutcome(item.outcomeSms),
    outcomeWhatsapp: parseBulkMessageEventOutcome(item.outcomeWhatsapp),
    outcomeEmail: parseBulkMessageEventOutcome(item.outcomeEmail),
    outcomeWao: parseBulkMessageEventOutcome(item.outcomeWao),
    outcomeDetails: readString(item.outcomeDetails),
    sendTime: readString(item.sendTime),
    bulkMessageChannel: parseBulkMessageChannel(item.bulkMessageChannel),
    branchCode: readString(item.branchCode),
    mediaUrl: readString(item.mediaUrl),
    waoTemplateName: readString(item.waoTemplateName),
    waoTemplateVariablesMap: readString(item.waoTemplateVariablesMap),
    queueStatus: parseBulkMessageQueueStatus(item.queueStatus),
    attemptCount: parseNumber(item.attemptCount),
    queuedAt: readString(item.queuedAt),
    processingAt: readString(item.processingAt),
    processedAt: readString(item.processedAt),
    apiUrl: readString(item.apiUrl),
    requestPayload: readString(item.requestPayload),
    responseBody: readString(item.responseBody),
    apiJobId: readString(item.apiJobId),
    apiArqJobId: readString(item.apiArqJobId),
    httpStatus: parseNumber(item.httpStatus),
    lastError: readString(item.lastError),
  };
}

export function normalizeBulkMessage(raw: unknown): BulkMessage | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  const bulkMessageCode = readString(item.bulkMessageCode).trim();

  if (!bulkMessageCode) {
    return null;
  }

  const events = Array.isArray(item.bulkMessageEvents)
    ? item.bulkMessageEvents
        .map((event) => normalizeBulkMessageEvent(event))
        .filter((event): event is BulkMessageEvent => event !== null)
    : [];

  return {
    bulkMessageId: parseNumber(item.bulkMessageId),
    bulkMessageName: readString(item.bulkMessageName),
    filterTags: readString(item.filterTags),
    branchCode: readString(item.branchCode),
    bulkMessageCode,
    createdBy: readString(item.createdBy),
    branchName: readString(item.branchName),
    templateWhatsApp: readString(item.templateWhatsApp),
    templateWhatsAppEnglish: readString(item.templateWhatsAppEnglish),
    templateSms: readString(item.templateSms),
    templateEmailTitle: readString(item.templateEmailTitle),
    templateEmailBody: readString(item.templateEmailBody),
    creationDate: readString(item.creationDate),
    bulkMessageStatus: parseBulkMessageStatus(item.bulkMessageStatus),
    bulkMessageChannel: parseBulkMessageChannel(item.bulkMessageChannel),
    bulkCustomerIdsList: readNumberArray(item.bulkCustomerIdsList),
    bulkCustomerCount: parseNumber(item.bulkCustomerCount),
    bulkMessageEvents: events,
    waoTemplateName: readString(item.waoTemplateName),
    waoTemplateLangs: readStringArray(item.waoTemplateLangs),
    waoTemplateVariablesMap: readString(item.waoTemplateVariablesMap),
    ctaBasePath: readString(item.ctaBasePath),
    ctaDynamicPath: readString(item.ctaDynamicPath),
    whatsappPhotoUrl: readString(item.whatsappPhotoUrl),
  };
}

export function normalizeBulkMessagesList(raw: unknown): BulkMessage[] {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => normalizeBulkMessage(item))
      .filter((item): item is BulkMessage => item !== null);
  }

  // Branch retrieve can return a Spring page: { content: BulkMessage[] }
  if (raw && typeof raw === 'object' && 'content' in raw) {
    const { content } = raw;
    if (Array.isArray(content)) {
      return content
        .map((item) => normalizeBulkMessage(item))
        .filter((item): item is BulkMessage => item !== null);
    }
  }

  return [];
}

export function normalizeBulkMessageMultimedia(
  raw: unknown,
): BulkMessageMultimedia {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 0,
      fileName: '',
      filePath: '',
      fileType: '',
      fileUrl: '',
      storedDate: '',
    };
  }

  const item = raw as Record<string, unknown>;

  return {
    id: parseNumber(item.id),
    fileName: readString(item.fileName),
    filePath: readString(item.filePath),
    fileType: readString(item.fileType),
    fileUrl: readString(item.fileUrl),
    storedDate: readString(item.storedDate),
  };
}

export function normalizeBulkMessageQueueStatus(
  raw: unknown,
): BulkMessageQueueStatusDto {
  if (!raw || typeof raw !== 'object') {
    return {
      queueName: '',
      messageCount: 0,
      consumerCount: 0,
      consumerRunning: false,
      totalEvents: 0,
      totalSendEvents: '0',
      totalPendingEvents: '0',
      totalFailedEvents: '0',
    };
  }

  const item = raw as Record<string, unknown>;

  return {
    queueName: readString(item.queueName),
    messageCount: parseNumber(item.messageCount),
    consumerCount: parseNumber(item.consumerCount),
    consumerRunning: parseBoolean(item.consumerRunning),
    totalEvents: parseNumber(item.totalEvents),
    totalSendEvents: readString(item.totalSendEvents) || '0',
    totalPendingEvents: readString(item.totalPendingEvents) || '0',
    totalFailedEvents: readString(item.totalFailedEvents) || '0',
  };
}

export function normalizePageBulkMessageEvent(
  raw: unknown,
): PageBulkMessageEvent {
  if (!raw || typeof raw !== 'object') {
    return {
      content: [],
      totalPages: 0,
      totalElements: 0,
      numberOfElements: 0,
      size: 0,
      number: 0,
      first: true,
      last: true,
      empty: true,
    };
  }

  const item = raw as Record<string, unknown>;
  const content = Array.isArray(item.content)
    ? item.content
        .map((event) => normalizeBulkMessageEvent(event))
        .filter((event): event is BulkMessageEvent => event !== null)
    : [];

  return {
    content,
    totalPages: parseNumber(item.totalPages),
    totalElements: parseNumber(item.totalElements),
    numberOfElements: parseNumber(item.numberOfElements, content.length),
    size: parseNumber(item.size),
    number: parseNumber(item.number),
    first: parseBoolean(item.first, true),
    last: parseBoolean(item.last, true),
    empty: parseBoolean(item.empty, content.length === 0),
  };
}

export function buildCreateBulkMessageBody(
  branchCode: string,
  bulkMessageName: string,
): CreateBulkMessagePayload {
  return {
    branchCode,
    bulkMessageName: bulkMessageName.trim(),
    bulkMessageChannel: 'WHATSAPP',
  };
}
